import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { usePredictions } from '@/api/generated';
import { SkuPredictionOverview } from '@/components/blocks/shelf-detail/predictions-overview/sku-prediction-overview';
import { SkuPredictionSkeleton } from '@/components/blocks/shelf-detail/shelf-detail-sku/shelf-detail-sku-predictions-skeleton';
import { Label } from '@/components/ui/label';

export const TOP_PREDICTIONS_LIMIT = 15;

interface ShelfDetailSkuPredictionProps {
  detectionIds: Array<string>;
  confirmedSkuMetadataId?: string;
  setSkuForDetection: (skuMetadataId: string) => void;
  noneSelected: boolean;
  onNoneOfTheAbove: () => void;
  className?: string;
}

export const ShelfDetailSkuPrediction: FC<ShelfDetailSkuPredictionProps> = ({
  detectionIds,
  confirmedSkuMetadataId,
  setSkuForDetection,
  noneSelected,
  onNoneOfTheAbove,
  className,
}: ShelfDetailSkuPredictionProps) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = usePredictions(
    { detectionId: detectionIds, limit: TOP_PREDICTIONS_LIMIT },
    { query: { enabled: detectionIds.length > 0 } }
  );

  const items = data?.items ?? [];

  if (isLoading) {
    return <SkuPredictionSkeleton />;
  }

  if (isError) {
    return <Label>{t('common.error')}</Label>;
  }
  // With no predictions, "None of the above" is still the way to mark the
  // box as a missing SKU.
  return (
    <>
      {items.length === 0 && <Label>{t('shelfDetail.noPredictions')}</Label>}
      <SkuPredictionOverview
        classname={className}
        confirmedSkuMetadataId={confirmedSkuMetadataId}
        predictions={items}
        onConfirmSkuPrediction={setSkuForDetection}
        noneSelected={noneSelected}
        onNoneOfTheAbove={onNoneOfTheAbove}
      />
    </>
  );
};
